<template>
  <div class="h-full w-full flex items-center justify-center px-6 py-8 bg-white/90">
    <div class="w-full max-w-[280px]">
      <label class="b-heading-m block mb-6">
        {{ actionText }}
      </label>
      <div class="mb-6 flex items-center justify-center gap-4">
        <div
          v-for="idx in 4"
          :key="idx"
          class="h-4 w-4 rounded-full transition-colors"
          :class="idx <= pin.length ? 'bg-gray-500' : 'bg-gray-300'"
        />
      </div>

      <div class="grid grid-cols-3 gap-5">
        <b-button
          v-for="digit in digits"
          :key="digit"
          type="button"
          class="h-12 w-12 justify-self-center rounded-full border border-gray-400 text-2xl font-medium text-gray-900 transition hover:bg-gray-50 active:scale-95"
          @click="handleDigit(digit)"
        >
          {{ digit }}
        </b-button>

        <b-button
          icon="backspace"
          class="h-12 w-12 justify-self-center rounded-full"
          aria-label="Go back"
          @click="handleBack"
        />

        <b-button
          type="button"
          class="h-12 w-12 justify-self-center rounded-full border border-gray-400 text-2xl font-medium text-gray-900 transition hover:bg-gray-50 active:scale-95"
          @click="handleDigit(0)"
        >
          0
        </b-button>

        <b-button
          icon="check"
          class="h-12 w-12 justify-self-center rounded-full"
          :primary="isPinCorrect"
          :disabled="!isPinCorrect"
          aria-label="Confirm PIN"
          @click="handleConfirm"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, shallowRef } from 'vue'
import { BButton } from '@booksy-com/vue-components-core'

import { useLoadingScreen } from '@/composables/loading-screen'
import { useToast } from '@/composables/toast'
import { useCore } from '@/composables/core'

import { useTurnTrackerCheckInMode } from '../composables/check-in-mode'

const props = defineProps<{
  mode: 'validate' | 'setup'
}>()

const emit = defineEmits(['resolve'])

const { t } = useCore()

const { setupPinCode, validatePinCode } = useTurnTrackerCheckInMode()

const ACTION_TEXT = {
  ENTER_PIN: 'Enter PIN',
  SET_NEW_PIN: 'Set new PIN',
  CONFIRM_NEW_PIN: 'Confirm new PIN',
}
const actionText = shallowRef(props.mode === 'validate' ? ACTION_TEXT.ENTER_PIN : ACTION_TEXT.SET_NEW_PIN)

const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9]
const pin = ref('')
const previousPin = ref('')

const handleDigit = (digit) => {
  if (pin.value.length >= 4) return

  pin.value += String(digit)
}

const handleBack = () => {
  pin.value = pin.value.slice(0, -1)
}

const isPinCorrect = computed(() => pin.value.length === 4)

const wrongPinText = `Wrong PIN`

const loadingScreen = useLoadingScreen()
const toast = useToast()

const handleConfirm = async () => {
  if (props.mode === 'validate') {
    loadingScreen.show()
    try {
      const result = await validatePinCode({
        pin: Number(pin.value),
      })

      pin.value = ''

      if (result.status === 'ok') {
        emit('resolve')
        return
      }

      toast.error(wrongPinText)

    } catch (e) {
      toast.error(t('error'))
      pin.value = ''
    } finally {
      loadingScreen.hide()
    }
  }

  if (props.mode === 'setup') {
    if (pin.value.length === 4 && previousPin.value.length === 0) {
      previousPin.value = pin.value
      pin.value = ''
      actionText.value = ACTION_TEXT.CONFIRM_NEW_PIN
      return
    }

    if (pin.value !== previousPin.value) {
      toast.error(wrongPinText)
      pin.value = ''
      return
    }

    loadingScreen.show()
    try {
      await setupPinCode({
        pin: Number(pin.value),
      })
      emit('resolve')
      toast.success(t('success'))
    } catch (e) {
      toast.error(t('error'))
    } finally {
      loadingScreen.hide()
    }
  }
}
</script>
